import type { AasApiAdapter, InviteMemberInput, UpdateWorkspaceInput } from "../adapter";
import { requestJson } from "../http";
import type {
	BackendWorkspace,
	BetterAuthInvitation,
	BetterAuthListMembersResponse,
} from "./backendTypes";
import { asObject, toDateOnly, toNumber, toRole } from "./shared";

function mapWorkspace(workspace: BackendWorkspace) {
	const limits = asObject(workspace.rateLimits);
	return {
		name: workspace.name,
		slug: workspace.slug,
		rateLimits: {
			tokensPerMinute: toNumber(limits.tokens_per_minute),
			requestsPerMinute: toNumber(limits.requests_per_minute),
			maxConcurrentSessions: toNumber(limits.max_concurrent),
		},
		createdAt: workspace.createdAt,
	};
}

export const settingsApi: AasApiAdapter["settings"] = {
	async workspace() {
		return mapWorkspace(await requestJson<BackendWorkspace>("/api/workspaces/current"));
	},
	async updateWorkspace(input: UpdateWorkspaceInput) {
		const body: Record<string, unknown> = {};
		if (input.name !== undefined) body.name = input.name;
		if (input.rateLimits) {
			body.rateLimits = {
				tokens_per_minute: input.rateLimits.tokensPerMinute,
				requests_per_minute: input.rateLimits.requestsPerMinute,
				max_concurrent: input.rateLimits.maxConcurrentSessions,
			};
		}
		return mapWorkspace(
			await requestJson<BackendWorkspace>("/api/workspaces/current", { method: "PATCH", body }),
		);
	},
	async members() {
		const response = await requestJson<BetterAuthListMembersResponse>(
			"/api/auth/organization/list-members",
		);
		return response.members.map((member) => ({
			id: member.id,
			userId: member.user?.id ?? member.id,
			name: member.user?.name ?? member.user?.email ?? "Unknown user",
			email: member.user?.email ?? "unknown@example.com",
			role: toRole(member.role),
			joinedAt: toDateOnly(member.createdAt),
		}));
	},
	async invitations() {
		const invitations = await requestJson<BetterAuthInvitation[]>(
			"/api/auth/organization/list-invitations",
		);
		return invitations
			.filter((invite) => invite.status === "pending")
			.map((invite) => ({
				id: invite.id,
				email: invite.email,
				role: toRole(invite.role),
				sentAt: toDateOnly(invite.createdAt),
				expiresAt: toDateOnly(invite.expiresAt),
			}));
	},
	async invite(input: InviteMemberInput) {
		const invite = await requestJson<BetterAuthInvitation>("/api/auth/organization/invite-member", {
			method: "POST",
			body: {
				email: input.email,
				role: input.role,
			},
		});
		return {
			id: invite.id,
			email: invite.email,
			role: toRole(invite.role),
			sentAt: toDateOnly(invite.createdAt),
			expiresAt: toDateOnly(invite.expiresAt),
		};
	},
	async updateMemberRole(memberId, role) {
		await requestJson("/api/auth/organization/update-member-role", {
			method: "POST",
			body: { memberId, role },
		});
	},
	async removeMember(memberId) {
		await requestJson("/api/auth/organization/remove-member", {
			method: "POST",
			body: { memberIdOrEmail: memberId },
		});
	},
	async cancelInvitation(invitationId) {
		await requestJson("/api/auth/organization/cancel-invitation", {
			method: "POST",
			body: { invitationId },
		});
	},
};
